import React, { useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowLeft, Phone, MessageCircle } from "lucide-react";
import SEO from "@/components/SEO";
import { COMPANY } from "@/data";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

// Google Ads conversion event "Demande de devis" (AW-18463610714)
// The named event is auto-routed to the correct conversion action by Google Ads
// as long as the global gtag config for AW-18463610714 is loaded (done in index.html).
const GADS_CONVERSION_EVENT = "ads_conversion_Demande_de_devis_1";

const fireQuoteConversion = () => {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag("event", GADS_CONVERSION_EVENT, {});
};

const Confirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const fired = useRef(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });

    // Only fire the Google Ads conversion when the user arrives here right after
    // a successful form submission (fromForm flag pushed by QuoteForm's navigate()).
    // Direct visits, refreshes, or bookmarks will NOT trigger the conversion.
    // useRef guard guarantees exactly ONE fire per mount, even under React StrictMode.
    if (fired.current) return;
    if (location.state && location.state.fromForm) {
      fired.current = true;
      fireQuoteConversion();
      // Clear the state so a refresh doesn't re-trigger the conversion.
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  return (
    <>
      <SEO
        title="Demande envoyée"
        description="Votre demande de devis a bien été envoyée à VistaWash."
        path="/demande-envoyee"
        noindex
      />

      <section
        data-testid="confirmation-page"
        className="relative min-h-screen flex items-center justify-center px-6 md:px-12 py-32 bg-black grain overflow-hidden"
      >
        {/* Background subtle red glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] bg-[#D90000]/10 blur-[180px] rounded-full" />
        </div>

        <div className="relative z-10 max-w-3xl mx-auto text-center">
          {/* Success mark */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="inline-flex items-center justify-center mb-12"
            data-testid="confirmation-checkmark"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-[#D90000] blur-2xl opacity-40 animate-pulse" />
              <div className="relative w-24 h-24 md:w-28 md:h-28 border-2 border-[#D90000] bg-black flex items-center justify-center">
                <Check size={44} className="text-[#D90000]" strokeWidth={2.5} />
              </div>
            </div>
          </motion.div>

          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={1}>
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">
              — Message reçu
            </span>
          </motion.div>

          <motion.h1
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={2}
            data-testid="confirmation-title"
            className="font-impact text-5xl sm:text-6xl md:text-7xl lg:text-[110px] leading-[0.9] mt-6"
          >
            MERCI ! <br />
            <span className="text-[#D90000]">VOTRE DEMANDE</span> <br />
            EST BIEN REÇUE.
          </motion.h1>

          <motion.p
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={3}
            data-testid="confirmation-message"
            className="text-white/70 text-base md:text-lg leading-relaxed mt-10 max-w-2xl mx-auto"
          >
            Merci ! Votre demande est bien reçue. VistaWash vous recontacte très vite pour fixer votre
            diagnostic gratuit de 15 min à l'atelier.
          </motion.p>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={4}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-14"
          >
            <a
              href={COMPANY.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="confirmation-whatsapp-btn"
              className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1ebe5a] text-black text-[12px] uppercase tracking-[0.22em] font-bold px-10 py-5 transition-colors border border-[#25D366]"
            >
              <MessageCircle size={16} />
              Continuer sur WhatsApp
            </a>

            <Link
              to="/"
              data-testid="confirmation-home-btn"
              className="group inline-flex items-center gap-3 border border-white/25 hover:border-white hover:bg-white hover:text-black text-white text-[12px] uppercase tracking-[0.22em] font-bold px-10 py-5 transition-all"
            >
              <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
              Retour à l'accueil
            </Link>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={5}
            className="mt-16 pt-10 border-t border-white/10 max-w-md mx-auto"
          >
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">
              Une urgence ?
            </div>
            <a
              href={COMPANY.phone_link}
              data-testid="confirmation-phone"
              className="inline-flex items-center gap-3 text-white hover:text-[#D90000] transition-colors font-impact text-2xl md:text-3xl"
            >
              <Phone size={20} className="text-[#D90000]" />
              {COMPANY.phone_display}
            </a>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Confirmation;
